import { X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';

/**
 * ConfirmationModal Component
 *
 * A reusable modal dialog that prompts users to confirm or cancel an action.
 * Built using AlertDialog components for accessibility and consistent styling.
 *
 * Features:
 * - Controlled visibility state
 * - Customizable title and description
 * - Standardized cancel and confirm buttons
 * - Accessible design with proper ARIA attributes
 * - High z-index to ensure visibility above other UI elements
 * - Responsive layout with maximum width
 *
 * Props:
 * @param {boolean} open - Controls whether the modal is displayed
 * @param {(open: boolean) => void} onOpenChange - Callback when modal open state changes
 * @param {string} title - The title displayed in the modal header
 * @param {string} description - The descriptive text explaining the action to confirm
 * @param {() => void} onConfirm - Callback function executed when the user confirms the action
 *
 * @example
 * // Basic usage
 * <ConfirmationModal
 *   open={showDeleteDialog}
 *   onOpenChange={setShowDeleteDialog}
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item? This action cannot be undone."
 *   onConfirm={handleDelete}
 * />
 */

export interface ConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  onConfirm: () => unknown;
  confirmText?: string;
  cancelText?: string;
  confirmClassName?: string;
  /**
   * If true, the modal will not automatically close after confirmation.
   * The parent component is responsible for closing the modal if needed.
   * Default is false (modal will auto-close after confirmation).
   */
  preventAutoClose?: boolean;
  /** Hide the cancel button (e.g. an acknowledgement-only dialog). */
  hideCancel?: boolean;
  /** Extra content under the description (e.g. a required reason field). */
  children?: React.ReactNode;
  /** Disables the confirm button (e.g. until a required field is filled). */
  confirmDisabled?: boolean;
  /** Shows a spinner on the confirm button and disables it (e.g. while the action runs). */
  confirmLoading?: boolean;
}

export const ConfirmationModal = ({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmClassName,
  preventAutoClose = false,
  hideCancel = false,
  children,
  confirmDisabled = false,
  confirmLoading = false,
}: Readonly<ConfirmationModalProps>) => {
  const { t } = useTranslation();

  const handleConfirmClick = () => {
    // onConfirm may reject (e.g. a mutation the caller doesn't await); the mutation's own
    // global error handler already shows a toast, so just prevent an unhandled rejection here.
    Promise.resolve(onConfirm()).catch(() => {
      // Error toast is already shown by the mutation's global error handler.
    });
    if (!preventAutoClose) {
      onOpenChange(false);
    }
  };
  const handleCancelClick = () => {
    onOpenChange(false);
  };

  const isDestructiveConfirm = Boolean(
    confirmClassName?.includes('bg-destructive') || confirmClassName?.includes('destructive')
  );

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="z-[100] max-w-[420px] gap-0 overflow-hidden rounded-xl border-0 p-0 shadow-e4">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <AlertDialogTitle className="min-w-0 text-lg font-semibold text-neutral-900 [overflow-wrap:anywhere]">
            {title}
          </AlertDialogTitle>
          <button
            type="button"
            onClick={handleCancelClick}
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            aria-label="Close"
          >
            <X className="size-[18px]" aria-hidden />
          </button>
        </div>

        <AlertDialogDescription asChild>
          <div className="px-5 py-4 text-sm text-neutral-600 [overflow-wrap:anywhere]">
            {description}
          </div>
        </AlertDialogDescription>
        {children && <div className="px-5 pb-4">{children}</div>}

        <AlertDialogFooter className="flex-row justify-end gap-2 border-t border-neutral-200 px-5 py-4 sm:justify-end">
          {!hideCancel && (
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancelClick}
              className="px-4 text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900"
            >
              {t(cancelText)}
            </Button>
          )}
          <Button
            type="button"
            variant={isDestructiveConfirm ? 'destructive' : 'default'}
            className={cn(
              'px-4',
              !isDestructiveConfirm && 'bg-primary-600 hover:bg-primary-700',
              !isDestructiveConfirm && confirmClassName
            )}
            onClick={handleConfirmClick}
            loading={confirmLoading}
            disabled={confirmDisabled || confirmLoading}
          >
            {t(confirmText)}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
